import { skipToken } from "@reduxjs/toolkit/query";
import {
  AutomationTemplateIds,
  automationTemplateIdSchema,
  buildDefaultAutomationSettings,
  getAutomationTemplateById,
  type AutomationId,
  type AutomationTemplateId,
} from "@repo/app-types/entities/automation";
import type { AutomationConfiguration } from "@repo/app-types/entities/automationProposal";
import {
  copilotProposalIdSchema,
  type CopilotProposal,
} from "@repo/app-types/entities/copilotProposal";
import {
  asPlaybookId,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import { FieldError } from "@repo/ui/field/fieldError";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { useRef, useState, type FC } from "react";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import { v4 as uuidv4 } from "uuid";
import {
  useCreateAutomationMutation,
  useLazyAutomationByIdQuery,
} from "../../api/entities/automation.js";
import {
  useGetPendingAutomationProposalQuery,
  useResolveProposalMutation,
} from "../../api/entities/copilotProposal.js";
import { isConflictQueryError } from "../../api/queryErrors.js";
import { appPaths } from "../../app/appPaths.js";
import { requireRouteParam } from "../../app/routeParams.js";
import {
  PageBody,
  PageLayout,
  PageLayoutHeight,
} from "../../components/layout/pageLayout.js";
import { ResourceSaveFooter } from "../../components/resourceSaveFooter.js";
import { AutomationConfigForm } from "../../features/automations/automationConfigForm.js";
import {
  AUTOMATION_PROPOSAL_QUERY_PARAM,
  AUTOMATION_TEMPLATE_QUERY_PARAM,
} from "../../features/automations/automationEditorUrlState.js";
import type { AutomationFieldsContext } from "../../features/automations/automationSettingsFields.js";
import { AutomationTemplateDescription } from "../../features/automations/automationTemplateDescription.js";
import { CoachingPlaybookConflictModal } from "../../features/automations/coachingPlaybookConflictModal.js";
import { useAutomationFieldsContext } from "../../features/automations/useAutomationFieldsContext.js";
import { useReportEditorProposal } from "../../features/researchAgent/editorProposalsSlice.js";
import { checkIsVoiceAgentOption } from "../../hooks/usePlaybookAndVoiceAgentOptions.js";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard.js";
import { checkIsAutomationConfigDirty } from "./automationConfigDirty.js";

type CreateAutomationProposal = CopilotProposal<"automation"> & {
  data: { kind: "create_automation" };
};

/**
 * The new-automation editor. It opens seeded from a template's defaults
 * (?template=..., from the template chooser) or from a new automation Ask
 * Alpharun suggests (?proposalId=..., from its proposal card). Save creates
 * the automation, disabled, and opens it; Cancel returns to the list and
 * leaves a suggestion pending. Without either, the page sends the user to
 * the automation list.
 */
export const AutomationNewPage: FC = () => {
  const playbookId = asPlaybookId(requireRouteParam(useParams(), "playbookId"));
  const automationsHref = appPaths.playbookAutomations.build({ playbookId });
  const [searchParams] = useSearchParams();
  const proposalId = copilotProposalIdSchema.safeParse(
    searchParams.get(AUTOMATION_PROPOSAL_QUERY_PARAM),
  ).data;
  const templateId = automationTemplateIdSchema.safeParse(
    searchParams.get(AUTOMATION_TEMPLATE_QUERY_PARAM),
  ).data;
  const { currentData: proposal, isError } =
    useGetPendingAutomationProposalQuery(proposalId ?? skipToken);
  const fieldsContext = useAutomationFieldsContext();

  if (proposalId) {
    if (isError) {
      return <UnavailableSuggestionPage />;
    }
    if (!proposal) {
      return <LoadingPage />;
    }
    const { data } = proposal;
    if (data.kind !== "create_automation") {
      return <UnavailableSuggestionPage />;
    }
    return (
      <AutomationNewPageContent
        key={proposal.id}
        playbookId={playbookId}
        templateId={data.template_id}
        proposal={{ ...proposal, data }}
        automationsHref={automationsHref}
        fieldsContext={fieldsContext}
      />
    );
  }

  if (!templateId) {
    return <Navigate to={automationsHref} replace />;
  }
  // A coaching automation defaults to a playbook without one, so the seed
  // waits for the playbooks.
  if (fieldsContext.arePlaybooksLoading) {
    return <LoadingPage />;
  }
  return (
    <AutomationNewPageContent
      key={templateId}
      playbookId={playbookId}
      templateId={templateId}
      automationsHref={automationsHref}
      fieldsContext={fieldsContext}
    />
  );
};

const LoadingPage: FC = () => (
  <div className="flex min-h-full items-center justify-center">
    <Loader style={LoaderStyle.ZOOMIES} />
  </div>
);

const UnavailableSuggestionPage: FC = () => (
  <PageLayout>
    <PageHeader title="New automation" />
    <PageBody>
      <p className="text-sm text-muted-foreground">
        This suggestion is no longer available.
      </p>
    </PageBody>
  </PageLayout>
);

interface AutomationNewPageContentProps {
  playbookId: PlaybookId;
  templateId: AutomationTemplateId;
  /** The suggestion in the draft, which Save applies. */
  proposal?: CreateAutomationProposal;
  automationsHref: string;
  fieldsContext: AutomationFieldsContext;
}

const AutomationNewPageContent: FC<AutomationNewPageContentProps> = (props) => {
  const { playbookId, templateId, proposal, automationsHref, fieldsContext } =
    props;
  const {
    isReadOnly,
    playbookAndVoiceAgentOptions,
    arePlaybooksLoading,
    hasLoadedAllPlaybookOptions,
    areRecipientOptionsLoading,
    teammateOptions,
    editorAndReviewerTeammateOptions,
    groupOptions,
    managerTeammateIds,
    coachingPlaybookIdsInUse,
  } = fieldsContext;
  const navigate = useNavigate();
  const [createAutomation, { isLoading: isCreating }] =
    useCreateAutomationMutation();
  const [resolveProposal] = useResolveProposalMutation();
  const [fetchAutomation, { isFetching: isCheckingAutomation }] =
    useLazyAutomationByIdQuery();

  // The template's defaults: what the draft is unchanged from. A playbook has
  // one coaching automation, so a coaching draft defaults to the first
  // playbook without one.
  const [defaults] = useState<AutomationConfiguration>(() => {
    const defaultPlaybookId =
      templateId === AutomationTemplateIds.TEAMMATE_COACHING
        ? (playbookAndVoiceAgentOptions.find(
            (option) =>
              !checkIsVoiceAgentOption(option) &&
              !coachingPlaybookIdsInUse?.includes(option.value),
          )?.value ?? playbookId)
        : playbookId;
    return {
      name: getAutomationTemplateById(templateId).name,
      settings: buildDefaultAutomationSettings(templateId, defaultPlaybookId),
    };
  });
  const [name, setName] = useState(proposal?.data.name ?? defaults.name);
  const [settings, setSettings] = useState(
    proposal?.data.settings ?? defaults.settings,
  );
  const [hasCoachingConflict, setHasCoachingConflict] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sessionIdRef = useRef(uuidv4());

  const effectiveName = name.trim() || defaults.name;
  const isDirty = checkIsAutomationConfigDirty(
    { name: effectiveName, settings },
    defaults,
  );
  const isSaving = isCreating || isCheckingAutomation;
  useReportEditorProposal(proposal?.id, isSaving);

  const { guardModal, navigateWithoutConfirmation } =
    useUnsavedChangesGuard(isDirty);

  // Create first, then resolve: a failed create leaves the suggestion pending,
  // so its card can be reopened. A create that already went through collides
  // on the minted id, and Save opens that automation instead.
  const handleSave = async () => {
    setError(null);
    let automationId: AutomationId;
    try {
      const created = await createAutomation({
        template_id: templateId,
        name: effectiveName,
        is_enabled: false,
        settings,
        session_id: sessionIdRef.current,
        ...(proposal ? { proposal_id: proposal.id } : {}),
      }).unwrap();
      automationId = created.id;
    } catch (createError) {
      if (!isConflictQueryError(createError)) {
        setError("The automation couldn't be created. Please try again.");
        return;
      }
      // The coaching check runs before the insert, so a repeat Save of a
      // coaching suggestion conflicts with its own automation. Any other
      // conflict is the minted id already taken.
      const existing = proposal
        ? (await fetchAutomation(proposal.resource_id)).data
        : undefined;
      if (!existing) {
        setHasCoachingConflict(true);
        return;
      }
      automationId = existing.id;
    }
    // The automation exists either way, so it opens whether or not the
    // suggestion could be marked as applied.
    // TODO: Apply the proposal in the create's transaction on the server instead.
    if (proposal) {
      try {
        await resolveProposal({
          proposalId: proposal.id,
          status: "applied",
        }).unwrap();
      } catch (resolveError) {
        if (!isConflictQueryError(resolveError)) {
          setError(
            "The automation was created, but the suggestion could not be marked as applied.",
          );
        }
      }
    }
    navigateWithoutConfirmation(() => {
      void navigate(
        appPaths.playbookAutomation.build({ playbookId, automationId }),
        { replace: true },
      );
    });
  };

  const handleCancel = () => {
    navigateWithoutConfirmation(() => {
      void navigate(automationsHref);
    });
  };

  return (
    <PageLayout height={PageLayoutHeight.Fills}>
      {guardModal}
      <PageHeader title={name.trim() || "New automation"} />

      <PageBody fills>
        <AutomationTemplateDescription templateId={templateId} />

        <AutomationConfigForm
          templateId={templateId}
          isReadOnly={isReadOnly}
          playbookAndVoiceAgentOptions={playbookAndVoiceAgentOptions}
          arePlaybooksLoading={arePlaybooksLoading}
          hasLoadedAllPlaybookOptions={hasLoadedAllPlaybookOptions}
          areRecipientOptionsLoading={areRecipientOptionsLoading}
          teammateOptions={teammateOptions}
          editorAndReviewerTeammateOptions={editorAndReviewerTeammateOptions}
          groupOptions={groupOptions}
          managerTeammateIds={managerTeammateIds}
          coachingPlaybookIdsInUse={coachingPlaybookIdsInUse}
          name={name}
          settings={settings}
          onNameChange={setName}
          onSettingsChange={setSettings}
          onBlur={() => {
            setName(effectiveName);
          }}
        />

        {error ? <FieldError>{error}</FieldError> : null}

        <ResourceSaveFooter
          className="sticky bottom-0 z-10 -mx-4 mt-auto -mb-4 sm:-mx-6 sm:-mb-6"
          onCancel={handleCancel}
          onSave={() => {
            void handleSave();
          }}
          isSaving={isSaving}
          isSaveDisabled={isReadOnly}
        />
      </PageBody>

      <CoachingPlaybookConflictModal
        isOpen={hasCoachingConflict}
        onClose={() => {
          setHasCoachingConflict(false);
        }}
      />
    </PageLayout>
  );
};
